import React from 'react';
import { useArtifacts } from '@/contexts/ArtifactContext';
import ArtifactPanel from '@/components/ArtifactPanel';
import EmissaryOptimized from '@/components/EmissaryOptimized';
import { Card, CardContent } from "@/components/ui/card";
import { LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { User } from 'firebase/auth';

interface ChatWithArtifactsProps {
  sharedProps: any;
  currentUser: User | null;
  showLoginDialog: boolean;
  setShowLoginDialog: (show: boolean) => void;
}

export const ChatWithArtifacts: React.FC<ChatWithArtifactsProps> = ({
  sharedProps,
  currentUser,
  showLoginDialog,
  setShowLoginDialog
}) => {
  const { artifacts, isPanelOpen, updateArtifact } = useArtifacts();
  const hasArtifacts = artifacts.size > 0;

  const showArtifactPanel = hasArtifacts && isPanelOpen;

  return (
    <div className="flex-1 flex flex-row overflow-hidden">
      {/* Chat Area - hidden on mobile when artifact panel is open */}
      <div className={`flex flex-col ${
        showArtifactPanel 
          ? 'hidden md:flex md:flex-1' // Hidden on mobile, visible on desktop
          : 'w-full' // Full width when no artifacts
      }`}>
        {currentUser ? (
          <EmissaryOptimized {...sharedProps} currentUser={currentUser} />
        ) : (
          <div className="flex-1 flex items-center justify-center p-6">
            <Card className="w-full max-w-md">
              <CardContent className="pt-6">
                <div className="text-center space-y-4">
                  <LogIn className="mx-auto h-12 w-12 text-primary" />
                  <h3 className="text-xl font-semibold">Authentication Required</h3>
                  <p className="text-muted-foreground">Please log in to access this conversation.</p>
                  <Button 
                    className="mt-2" 
                    onClick={() => setShowLoginDialog(true)}
                  >
                    Log In
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      
      {/* Artifact Panel - full width on mobile, shared space on desktop */}
      {hasArtifacts && (
        <div className={`transition-all duration-300 ${
          isPanelOpen 
            ? 'w-full md:w-[35%] lg:w-[40%] xl:w-[45%] md:min-w-[400px] md:max-w-[700px]' 
            : 'w-0'
        } overflow-hidden`}>
          <ArtifactPanel 
            onArtifactUpdate={updateArtifact}
            hideToggleButton={true}
            inline={true}
            className="md:border-l"
          />
        </div>
      )}
    </div>
  );
};

export default ChatWithArtifacts;